/**
 * Call Centre ("Call campaigns") permissions.
 *
 * Module capability: Admin and CallCentreAdmin get everything. CallCentreSupervisor gets Read, Campaigns.Manage,
 * Import, Assign, Contacts.Edit, Journey.Manage and Handoffs.Manage (the seeded role rights in pg/identity/010),
 * but not Contacts.Export, Settings.Manage or Telephony.Manage. Any other user gets a capability only when they hold
 * the matching `CallCentre.*` right (rights come from the identity service and can be granted per user without a
 * role). Stage 3: the phone system page opens with Read; changing it (settings, test, diagnose, read call records
 * now, desk stations) needs Telephony.Manage. Stage 6: AI.Use (supervisors are seeded with it) opens the dashboard's
 * themes from recent calls.
 *
 * Note: the console API also requires one of the console roles (Admin, CallCentreAdmin, CallCentreSupervisor)
 * on top of the right, so a right on its own lets the page open but the API answers 403.
 *
 * The capability rules live in `computeCallCentrePermissions` (pure, no hooks) so the route gates
 * (CallCentreGate via useCallCentrePermissions) and the sidebar menu (callCentreMenu.tsx via
 * callCentreMenuVisibility) evaluate exactly the same thing. Which capability opens which page is
 * CALLCENTRE_PAGE_ACCESS; both the routes and the menu read it, so they cannot drift apart.
 */

import { useMemo } from 'react';
import { CALL_CENTRE_RIGHTS, CALL_CENTRE_ROLES } from '@actschurch/ccms-sdk';
import type { MenuItemVisibility } from '../../layouts/menu/types';
import { useEnrichedAuth } from '../useEnrichedAuth';

export { CALL_CENTRE_RIGHTS, CALL_CENTRE_ROLES };

export const CALLCENTRE_CONSOLE_ROLES = ['Admin', 'CallCentreAdmin', 'CallCentreSupervisor'];
export const CALLCENTRE_FULL_ACCESS_ROLES = ['Admin', 'CallCentreAdmin'];

interface CallCentreAccessChecks {
  hasRole: (role: string) => boolean;
  hasPermission: (right: string) => boolean;
}

/** Every Call Centre capability for a user, from plain role / right checks (no React). */
export function computeCallCentrePermissions({ hasRole, hasPermission }: CallCentreAccessChecks) {
  const isFullAccess = hasRole('Admin') || hasRole('CallCentreAdmin');
  const isSupervisor = hasRole('CallCentreSupervisor');

  /** Capability = full access, or the supervisor role when it grants it by default, or the explicit right. */
  const can = (right: string, supervisorDefault: boolean) => isFullAccess || (isSupervisor && supervisorDefault) || hasPermission(right);

  const canRead = can(CALL_CENTRE_RIGHTS.Read, true);
  const canManageCampaigns = can(CALL_CENTRE_RIGHTS.CampaignsManage, true);
  const canImport = can(CALL_CENTRE_RIGHTS.Import, true);
  const canAssign = can(CALL_CENTRE_RIGHTS.Assign, true);
  const canEditContacts = can(CALL_CENTRE_RIGHTS.ContactsEdit, true);
  const canExport = can(CALL_CENTRE_RIGHTS.ContactsExport, false);
  const canManageJourney = can(CALL_CENTRE_RIGHTS.JourneyManage, true);
  const canManageHandoffs = can(CALL_CENTRE_RIGHTS.HandoffsManage, true);
  const canManageSettings = can(CALL_CENTRE_RIGHTS.SettingsManage, false);
  const canManageTelephony = can(CALL_CENTRE_RIGHTS.TelephonyManage, false);
  /**
   * Stage 6: themes from recent calls (Admin / CallCentreAdmin or CallCentre.AI.Use, which the supervisor role is seeded
   * with). Each AI helper also needs its AIManagement flag and the call centre's AI switch (GET admin/ai/availability).
   */
  const canUseAi = can(CALL_CENTRE_RIGHTS.AiUse, true);

  return {
    canRead,
    canManageCampaigns,
    canImport,
    canAssign,
    canEditContacts,
    canExport,
    canManageJourney,
    canManageHandoffs,
    canManageSettings,
    canManageTelephony,
    canUseAi,
    isFullAccess,
    isSupervisor,
    /** Holds a console role (the API needs one of these on every admin call). */
    hasConsoleRole: isFullAccess || isSupervisor,
  };
}

export type CallCentreCapabilities = ReturnType<typeof computeCallCentrePermissions>;

export function useCallCentrePermissions() {
  const { user, hasRole, hasPermission } = useEnrichedAuth();

  const perms = useMemo(() => computeCallCentrePermissions({ hasRole, hasPermission }), [hasRole, hasPermission]);

  return {
    ...perms,
    userId: user?.id as string | undefined,
  };
}

export type CallCentrePermissions = ReturnType<typeof useCallCentrePermissions>;

/** Who may open a Call Centre page: one predicate per page, shared by the routes and the menu. */
export type CallCentrePageAccess = (p: CallCentreCapabilities) => boolean;

export const CALLCENTRE_PAGE_ACCESS = {
  dashboard: (p) => p.canRead,
  campaigns: (p) => p.canRead,
  campaignNew: (p) => p.canManageCampaigns,
  campaignSetup: (p) => p.canRead,
  campaignDetail: (p) => p.canRead,
  importReview: (p) => p.canRead,
  contacts: (p) => p.canRead,
  doNotCall: (p) => p.canRead,
  detailChanges: (p) => p.canRead,
  journey: (p) => p.canRead,
  handoffs: (p) => p.canRead,
  callers: (p) => p.canRead,
  reports: (p) => p.canRead,
  settings: (p) => p.canManageSettings,
  /** Phone system (Stage 3): connection, desk stations and call records; editing needs canManageTelephony. */
  telephony: (p) => p.canRead,
} satisfies Record<string, CallCentrePageAccess>;

/**
 * Menu visibility that matches the route gate: the menu context carries the same roles and rights
 * (user.roles / user.rights) that useEnrichedAuth checks, so a page shows in the menu exactly when
 * CallCentreGate would let the person in. Pass several pages for a section.
 */
export function callCentreMenuVisibility(...pages: CallCentrePageAccess[]): MenuItemVisibility {
  return (ctx) => {
    const roles = ctx.userRoles ?? [];
    const rights = ctx.userPermissions ?? [];
    const perms = computeCallCentrePermissions({
      hasRole: (role) => roles.includes(role),
      hasPermission: (right) => rights.includes(right),
    });
    return pages.some((allow) => allow(perms));
  };
}
